import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ShoppingCart, Sparkles, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@/hooks/useWallet';
import { toast } from 'sonner';
import { CreditWalletCard } from '@/components/dashboard/CreditWalletCard';
import { CreditUsageLedger } from '@/components/dashboard/CreditUsageLedger';

const CATEGORIES: { value: string; label: string; helper: string }[] = [
  { value: 'topup', label: 'Listing Credits', helper: 'Hot, Super Hot, Refresh, Signature, Story, Video Tour, Featured Project — buy what you need.' },
  { value: 'lead_wallet', label: 'Lead Packs', helper: 'Prepay your lead wallet and pay only for qualified buyer leads.' },
  { value: 'banner', label: 'Banner Ads', helper: 'Self-serve homepage and listings sidebar banners.' },
  { value: 'branding', label: 'Branding', helper: 'Verified badges and premium agency branding.' },
];

export default function CreditStore() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { balance: walletBalance, refetch: refetchWallet } = useWallet();
  const [params, setParams] = useSearchParams();
  const initialTab = (params.get('tab') === 'leads' ? 'lead_wallet' : params.get('tab')) || 'topup';
  const [tab, setTab] = useState(initialTab);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmPkg, setConfirmPkg] = useState<{ id: string; name: string; price: number; sku: string } | null>(null);
  const [liveBalance, setLiveBalance] = useState<number | null>(null);
  const [checkingBalance, setCheckingBalance] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['topup-packages', tab],
    queryFn: async () => {
      const { data } = await supabase
        .from('advertising_packages' as any)
        .select('id, sku, name, description, price_etb, credit_grants, duration_days, category')
        .eq('category', tab)
        .eq('is_active', true)
        .order('price_etb');
      return (data as any[]) || [];
    },
  });

  const requestPurchase = async (pkgId: string, price: number, sku: string, name: string) => {
    if (!user) return toast.error('Please sign in first');
    setCheckingBalance(true);
    setLiveBalance(null);
    setConfirmPkg({ id: pkgId, name, price, sku });
    const { data: w } = await supabase
      .from('wallets' as any)
      .select('balance_etb')
      .eq('user_id', user.id)
      .maybeSingle();
    const fresh = Number((w as any)?.balance_etb || 0);
    setLiveBalance(fresh);
    setCheckingBalance(false);
    qc.setQueryData(['wallet', user.id], (prev: any) => prev ? { ...prev, balance_etb: fresh } : prev);
  };

  const confirmPurchase = async () => {
    if (!confirmPkg || busy) return;
    const { id: pkgId, price, sku } = confirmPkg;
    // Lock UI immediately to block duplicate clicks
    setBusy(pkgId + ':wallet');
    // Re-verify balance immediately before charging
    const { data: w } = await supabase
      .from('wallets' as any)
      .select('balance_etb')
      .eq('user_id', user!.id)
      .maybeSingle();
    const fresh = Number((w as any)?.balance_etb || 0);
    setLiveBalance(fresh);
    if (fresh < price) {
      setBusy(null);
      toast.error(`Insufficient wallet balance. You have ${fresh.toLocaleString()} ETB but need ${price.toLocaleString()} ETB.`);
      refetchWallet();
      return;
    }
    setConfirmPkg(null);
    const { data, error } = await supabase.rpc('purchase_topup_with_wallet' as any, { _package_id: pkgId });
    setBusy(null);
    if (error) {
      if ((error.message || '').includes('INSUFFICIENT_FUNDS')) toast.error('Insufficient wallet balance.');
      else toast.error(error.message);
      refetchWallet();
      return;
    }
    toast.success(`${price.toLocaleString()} ETB deducted from wallet. Credits added! (${sku})`);
    qc.invalidateQueries({ queryKey: ['wallet'] });
    qc.invalidateQueries({ queryKey: ['wallet-txs-recent'] });
    qc.invalidateQueries({ queryKey: ['credit-wallet'] });
    qc.invalidateQueries({ queryKey: ['credit-ledger', user!.id] });
    navigate(`/dashboard/wallet/receipt/${data}`);
  };


  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShoppingCart className="h-6 w-6 text-primary" /> Credit Store
        </h1>
        <p className="text-sm text-muted-foreground">Buy à la carte credits, lead packs, banner ads, and premium branding.</p>
      </div>

      <Card className="bg-emerald-500/5 border-emerald-500/20">
        <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm">
            <Wallet className="h-4 w-4 text-emerald-600" />
            <span className="text-muted-foreground">Wallet balance:</span>
            <span className="font-semibold tabular-nums">{walletBalance.toLocaleString()} ETB</span>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/dashboard/wallet')}>Top Up Wallet</Button>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={(v) => { setTab(v); setParams({ tab: v }); }}>
        <TabsList className="flex-wrap h-auto">
          {CATEGORIES.map((c) => <TabsTrigger key={c.value} value={c.value}>{c.label}</TabsTrigger>)}
        </TabsList>

        {CATEGORIES.map((c) => (
          <TabsContent key={c.value} value={c.value} className="space-y-3">
            <p className="text-sm text-muted-foreground">{c.helper}</p>
            {isLoading ? <Skeleton className="h-40" /> : !data?.length ? (
              <Card><CardContent className="p-8 text-center text-muted-foreground">No packages available right now.</CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.map((p) => {
                  const grants = p.credit_grants && Object.keys(p.credit_grants).length > 0
                    ? Object.entries(p.credit_grants).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(', ')
                    : null;
                  return (
                    <Card key={p.id} className="relative hover:shadow-md transition-shadow">
                      <CardContent className="p-5 space-y-3">
                        <Badge variant="outline" className="text-[10px]">{p.sku}</Badge>
                        <p className="font-semibold">{p.name}</p>
                        <p className="text-2xl font-bold">{Number(p.price_etb).toLocaleString()}<span className="text-xs font-normal text-muted-foreground"> ETB</span></p>
                        {grants && <p className="text-xs text-muted-foreground flex items-center gap-1"><Sparkles className="h-3 w-3 text-primary" /> {grants}</p>}
                        {p.description && <p className="text-xs text-muted-foreground">{p.description}</p>}
                        <Button size="sm" className="w-full gap-1"
                          disabled={!!busy || walletBalance < Number(p.price_etb)}
                          onClick={() => requestPurchase(p.id, Number(p.price_etb), p.sku, p.name)}>
                          <Wallet className="h-3.5 w-3.5" />
                          {busy === p.id + ':wallet' ? 'Processing…' : busy ? 'Please wait…' : 'Pay with Wallet'}
                        </Button>
                        {walletBalance < Number(p.price_etb) && (
                          <p className="text-[10px] text-amber-600">Top up {(Number(p.price_etb) - walletBalance).toLocaleString()} ETB more to buy this pack.</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Purchased credits balances + usage history */}
      <CreditWalletCard />
      <CreditUsageLedger />

      <AlertDialog open={!!confirmPkg} onOpenChange={(o) => { if (!o && !busy) { setConfirmPkg(null); setLiveBalance(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm credit purchase</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <div>You're about to buy <strong>{confirmPkg?.name}</strong> ({confirmPkg?.sku}).</div>
                <div className="flex justify-between border-t pt-2">
                  <span>Amount to deduct:</span>
                  <span className="font-semibold tabular-nums">{confirmPkg?.price.toLocaleString()} ETB</span>
                </div>
                <div className="flex justify-between">
                  <span>Latest wallet balance:</span>
                  <span className="tabular-nums">
                    {checkingBalance ? 'Checking…' : `${(liveBalance ?? walletBalance).toLocaleString()} ETB`}
                  </span>
                </div>
                {!checkingBalance && liveBalance !== null && confirmPkg && liveBalance < confirmPkg.price ? (
                  <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-destructive">
                    Insufficient balance. You need {(confirmPkg.price - liveBalance).toLocaleString()} ETB more. Top up your wallet to continue.
                  </div>
                ) : (
                  <div className="flex justify-between text-emerald-600">
                    <span>Balance after purchase:</span>
                    <span className="font-semibold tabular-nums">
                      {(((liveBalance ?? walletBalance)) - (confirmPkg?.price ?? 0)).toLocaleString()} ETB
                    </span>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmPurchase}
              disabled={!!busy || checkingBalance || (liveBalance !== null && !!confirmPkg && liveBalance < confirmPkg.price)}
            >
              {busy ? 'Processing…' : checkingBalance ? 'Checking balance…' : 'Confirm & Pay'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
